/**
 * PAINTED WALL FINISHES (2026-09-28, Matt: "use the paint bucket to add on finish
 * layers to walls … a typical exterior wall has studs and plywood to the exterior with
 * gypsum board to the interior. We want to be able to paint on the exterior material …
 * the paint on should match the actual material thickness, not just paint the face of
 * the plywood — it should add that layer to the assembly … a few should be modeled
 * precisely: board and batten siding and corrugated metal siding").
 *
 * A wall instance carries `finishExterior` / `finishInterior` (material ids). At
 * resolve time the painted finish becomes a real LAYER of the wall's resolved
 * assembly, at the material's own thickness (its CladdingSpec):
 *   - if the outermost layer on that side is already a cladding material (a type with
 *     a baked-in finish, e.g. "Ext 2×6 · Stucco"), the painted one REPLACES it;
 *   - otherwise it is ADDED outside that face (a sheathed wall gets its siding).
 * The STRUCTURE STAYS PUT: the resolved assembly carries `paintedFrom` (the type's own
 * assembly) and `paintShiftFt`, and `centerlineOffsetFt` places the wall from the
 * type assembly and then moves the centerline by the painted change — so the studs
 * never move whatever location line the wall uses; the finish grows outward.
 * Everything downstream (plan poché, sections, 3D faces, schedules of thickness,
 * dimensions to layer faces) reads the resolved assembly, so it all follows.
 */
import type { AssemblyRecord, CladdingSpec, MaterialId, MaterialRecord } from './types'

/** the curated exterior finishes, by NAME — the firm's existing wall standards plus
 *  corrugated metal. `thicknessFt` is the real material thickness. */
export const CLADDING_MATERIALS: { name: string; color: string; spec: CladdingSpec }[] = [
  { name: 'Stucco', color: '#b8b2a6', spec: { profile: 'flat', thicknessFt: 0.0729 } },                 // 7/8" three-coat
  // MATERIAL CLEANUP (2026-10-05): the flat Stone Veneer, Clapboard Siding and Shingle Siding are
  // gone (materialCleanup.ts) — the 3D stone patterns and the Hardie lap siding replace them
  // boards are the layer; the battens are modeled proud of it
  {
    name: 'Board & Batten Siding', color: '#8f7b5e',
    spec: { profile: 'boardBatten', thicknessFt: 0.0625, battenWidthFt: 2.5 / 12, battenDepthFt: 0.75 / 12, battenSpacingFt: 1 },
  },
  // the layer is the full corrugation depth; the sheet is modeled inside it
  {
    name: 'Corrugated Metal Siding', color: '#8d9398',
    spec: { profile: 'corrugated', thicknessFt: 0.875 / 12, pitchFt: 2.67 / 12 },
  },
  // VERTICAL WOOD SIDING (2026-10-02, Matt: "3/4" thick vertical stained wood siding in
  // a driftwood color … vertical lines on the visible plane at 6" spacing")
  {
    name: 'Vertical Wood Siding (Driftwood)', color: '#9d9384',
    spec: { profile: 'vertical', thicknessFt: 0.75 / 12, lineSpacingFt: 0.5 },
  },
  // BRICK COURSING (2026-10-04): brick veneers draw their bed + head joints (masonry.ts) —
  // modular running bond is the firm's common brick; a long thin brick in running bond too
  {
    name: 'Brick Veneer', color: '#c8beac',
    spec: { profile: 'masonry', thicknessFt: 0.125, masonry: { unit: 'modular', bond: 'running' }, bed: { thicknessFt: 0.5 / 12, materialName: 'Mortar Bed' } },
  },
  {
    name: 'Brick Veneer, Long Thin (Running Bond)', color: '#b9ab98',
    spec: { profile: 'masonry', thicknessFt: 0.125, masonry: { unit: 'longThin', bond: 'running' }, bed: { thicknessFt: 0.5 / 12, materialName: 'Mortar Bed' } },
  },
  // STONE PATTERNS (2026-10-05): real 3D stones in shades of one color over the mortar
  // (masonry.ts STONE_PATTERNS, bim-geometry stone3d.ts) — 1-1/2" adhered veneer on a 1/2" bed
  {
    name: 'Stone Veneer, Coursed Ashlar', color: '#c9bea8',
    spec: { profile: 'masonry', thicknessFt: 0.125, masonry: { pattern: 'ashlar' }, bed: { thicknessFt: 0.5 / 12, materialName: 'Mortar Bed' } },
  },
  {
    name: 'Stone Veneer, Random Ashlar', color: '#ab9e8c',
    spec: { profile: 'masonry', thicknessFt: 0.125, masonry: { pattern: 'randomAshlar' }, bed: { thicknessFt: 0.5 / 12, materialName: 'Mortar Bed' } },
  },
  {
    name: 'Stone Veneer, Ledgestone', color: '#958a7b',
    spec: { profile: 'masonry', thicknessFt: 0.125, masonry: { pattern: 'ledgestone' }, bed: { thicknessFt: 0.5 / 12, materialName: 'Mortar Bed' } },
  },
  // LAP SIDING (2026-10-05, Matt: Hardie Artisan, 6" — Artisan Lap: a 7-1/4" board at a 6"
  // exposure, 5/8" thick, twice HardiePlank's 5/16"). Real 3D courses (finishFaces.ts); the
  // corners (trim boards or mitered) are a PROJECT setting (sidingSettings.ts)
  {
    name: 'Fiber Cement Lap Siding, 6" (Hardie Artisan)', color: '#cfc8ba',
    spec: { profile: 'lap', thicknessFt: 0.625 / 12, boardWidthFt: 7.25 / 12, exposureFt: 6 / 12, kickFt: 0.25 / 12 },
  },
]

const BY_NAME = new Map(CLADDING_MATERIALS.map((c) => [c.name, c.spec]))

/** the cladding a material builds on a wall, or null when it is a paint-on material */
export function claddingSpecOf(m: Pick<MaterialRecord, 'name'> & { cladding?: CladdingSpec } | null | undefined): CladdingSpec | null {
  if (!m) return null
  const c = m.cladding
  if (c && (c.profile === 'flat' || c.profile === 'boardBatten' || c.profile === 'corrugated' || c.profile === 'vertical' || c.profile === 'masonry' || c.profile === 'lap') && c.thicknessFt > 0) return c
  return BY_NAME.get(m.name) || null
}

export type WallSide = 'exterior' | 'interior'

export interface PaintedAssembly extends AssemblyRecord {
  /** the type's own assembly this one was painted from */
  paintedFrom?: AssemblyRecord
  /** how far the painted change moves the centerline toward the EXTERIOR, feet
   *  (± along the wall's exterior direction) — keeps the core fixed */
  paintShiftFt?: number
}

/**
 * The assembly a wall really has once its painted finishes are applied. `getMat`
 * resolves a material id (for the cladding spec + the replace rule). Returns the
 * type assembly unchanged when nothing is painted or nothing resolves.
 */
export function applyWallFinishes(
  base: AssemblyRecord,
  finishes: { exterior?: string | null; interior?: string | null },
  getMat: (id: string) => MaterialRecord | null | undefined,
  /** the setting bed's material by name (CladdingSpec.bed) */
  findByName?: (name: string) => MaterialRecord | null | undefined,
): { assembly: PaintedAssembly; specs: Partial<Record<WallSide, { materialId: string; spec: CladdingSpec }>> } {
  const specs: Partial<Record<WallSide, { materialId: string; spec: CladdingSpec }>> = {}
  if (!base.layers.length) return { assembly: base, specs }
  let layers = base.layers.map((l) => ({ ...l }))
  let core = { ...base.coreBoundary }
  let dExt = 0, dInt = 0
  const tag: string[] = []
  const isCladding = (id: string) => !!claddingSpecOf(getMat(id))
  for (const side of ['exterior', 'interior'] as WallSide[]) {
    const id = finishes[side]
    if (typeof id !== 'string' || !id) continue
    const spec = claddingSpecOf(getMat(id))
    if (!spec) continue
    specs[side] = { materialId: id, spec }
    // the setting bed (when the spec has one): its own layer behind the finish, or
    // folded into the finish's thickness when no bed material can be found
    const bedT = spec.bed && spec.bed.thicknessFt > 0 ? spec.bed.thicknessFt : 0
    const bedMat = bedT
      ? ((spec.bed!.materialId && getMat(spec.bed!.materialId)) || findByName?.(spec.bed!.materialName || 'Mortar Bed') || null)
      : null
    const layer = { function: 'finish' as const, materialId: id as MaterialId, thicknessFt: spec.thicknessFt + (bedT && !bedMat ? bedT : 0) }
    const bedLayer = bedMat ? { function: 'finish' as const, materialId: bedMat.id as MaterialId, thicknessFt: bedT } : null
    const outer = side === 'exterior' ? 0 : layers.length - 1
    const replace = layers.length > 1 && layers[outer].function === 'finish' && isCladding(layers[outer].materialId)
      && !(outer >= core.start && outer <= core.end)
    const added = side === 'exterior' ? (bedLayer ? [layer, bedLayer] : [layer]) : (bedLayer ? [bedLayer, layer] : [layer])
    const addedT = added.reduce((t, l) => t + l.thicknessFt, 0)
    const delta = addedT - (replace ? layers[outer].thicknessFt : 0)
    if (side === 'exterior') {
      const shift = added.length - (replace ? 1 : 0)
      layers = [...added, ...layers.slice(replace ? 1 : 0)]
      core = { start: core.start + shift, end: core.end + shift }
      dExt += delta
    } else {
      layers = [...layers.slice(0, replace ? layers.length - 1 : layers.length), ...added]
      dInt += delta
    }
    tag.push(`${side[0]}:${id}`)
  }
  if (!tag.length) return { assembly: base, specs }
  const assembly: PaintedAssembly = {
    ...base,
    id: `${base.id}~${tag.join('~')}` as AssemblyRecord['id'],
    layers,
    coreBoundary: core,
    paintedFrom: base,
    paintShiftFt: (dExt - dInt) / 2,
  }
  return { assembly, specs }
}
